import Card from "@/UI/Card";
import { Timeline } from "antd";
import moment from "moment";
import { FaCheckCircle, FaClock, FaEdit } from "react-icons/fa";

export default function DealStageHistory({ deal, dealStages }) {
  if (!deal || !dealStages) {
    return (
      <Card className="border-none">
        <div className="p-6 text-center text-gray-500">
          No stage history available
        </div>
      </Card>
    );
  }

  const timelineItems = [];

  // Deal Created
  if (deal.createdAt) {
    const createdStage = dealStages.find((s) => s.id === deal.dealStageId);
    timelineItems.push({
      color: "blue",
      dot: <FaCheckCircle className="text-blue-500" />,
      children: (
        <div className="pl-4">
          <div className="font-semibold text-gray-800 dark:text-gray-200">
            Deal Created
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {moment(deal.createdAt).format("MMMM DD, YYYY [at] HH:mm")}
          </div>
          {createdStage && (
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              Initial Stage: <span className="font-medium">{createdStage.stageName}</span>
            </div>
          )}
        </div>
      ),
      timestamp: moment(deal.createdAt).valueOf(),
    });
  }

  // Stage History from database
  if (deal.stageHistory && Array.isArray(deal.stageHistory) && deal.stageHistory.length > 0) {
    deal.stageHistory.forEach((historyItem) => {
      const stage = historyItem.dealStage || dealStages.find((s) => s.id === historyItem.dealStageId);
      const previousStage = historyItem.previousStage || (historyItem.previousStageId ? dealStages.find((s) => s.id === historyItem.previousStageId) : null);
      const changedBy = historyItem.changedByUser;
      
      timelineItems.push({
        color: "green",
        dot: <FaEdit className="text-green-500" />,
        children: (
          <div className="pl-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200">
              Stage Changed: {stage?.stageName || "Unknown Stage"}
            </div>
            {previousStage && (
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                From: <span className="font-medium">{previousStage.stageName}</span> → To: <span className="font-medium">{stage?.stageName || "Unknown"}</span>
              </div>
            )}
            {historyItem.notes && (
              <div className="text-sm text-gray-700 dark:text-gray-300 mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                <span className="font-medium">Notes:</span> {historyItem.notes}
              </div>
            )}
            {changedBy && (
              <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                Changed by: <span className="font-medium">{changedBy.firstName} {changedBy.lastName}</span> ({changedBy.username})
              </div>
            )}
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              {moment(historyItem.createdAt).format("MMMM DD, YYYY [at] HH:mm")}
            </div>
          </div>
        ),
        timestamp: moment(historyItem.createdAt).valueOf(),
      });
    });
  }

  // Current Stage (if no history exists, show current stage)
  if (deal.dealStage && (!deal.stageHistory || deal.stageHistory.length === 0)) {
    timelineItems.push({
      color: "green",
      dot: <FaClock className="text-green-500" />,
      children: (
        <div className="pl-4">
          <div className="font-semibold text-gray-800 dark:text-gray-200">
            Current Stage
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            <span className="font-medium">{deal.dealStage.stageName}</span>
          </div>
          {deal.probability !== undefined && (
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              Probability: {deal.probability}%
            </div>
          )}
          {deal.updatedAt && (
            <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              Last updated: {moment(deal.updatedAt).format("MMMM DD, YYYY [at] HH:mm")}
            </div>
          )}
        </div>
      ),
      timestamp: deal.updatedAt ? moment(deal.updatedAt).valueOf() : moment(deal.createdAt).valueOf(),
    });
  }

  // Sort by timestamp (newest first)
  timelineItems.sort((a, b) => b.timestamp - a.timestamp);

  return (
    <Card className="border-none">
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            Stage History
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Track all stage changes and activities related to this deal
          </p>
        </div>
        
        {timelineItems.length > 0 ? (
          <Timeline
            mode="left"
            items={timelineItems}
            className="deal-stage-history"
          />
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No stage history available
          </div>
        )}
      </div>
    </Card>
  );
}

